import { ROUTES } from '../constants/routes';

/**
 * Router `state`: how a history entry was opened.
 *
 * {@link WorkspaceNavState} carries a navigation that opens an item FROM the
 * workspace (the canvas views and the sidebar). The shell shows the workspace sidebar on an artifact page only when the
 * history entry carries this, so an artifact reached from search, chat, Home,
 * the Protocols page, a notification or a pasted link shows no sidebar. Living
 * on the history entry (not in a store) is what keeps Back and Forward
 * consistent: each entry remembers how it was reached.
 */
export interface WorkspaceNavState {
  readonly fromWorkspace: true;
}

export const FROM_WORKSPACE_STATE: WorkspaceNavState = { fromWorkspace: true };

/** Whether a history entry's `state` says it was opened from the workspace. */
export function isFromWorkspace(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as Partial<WorkspaceNavState>).fromWorkspace === true
  );
}

/**
 * Navigation state for a link to a destination page that is the workspace
 * pre-filtered (Equipment is the workspace filtered to equipment). The
 * destination's filter is the page, not the user's choice, so it is not
 * remembered: without this, every collection opened afterwards inherited the
 * Equipment filter and showed the equipment splash. A Type filter the
 * user then edits there is theirs (that edit navigates without this state) and
 * is remembered; any other change there keeps the state.
 */
export const DESTINATION_VIEW_STATE = { workspaceViewDestination: true } as const;

/** Whether a history entry's `state` says it opened a destination page. */
export function isDestination(state: unknown): boolean {
  return (
    typeof state === 'object' &&
    state !== null &&
    (state as { workspaceViewDestination?: unknown }).workspaceViewDestination === true
  );
}

/**
 * The workspace's own routes: the root canvas, any collection, and Trash (the
 * sidebar links to it, so opening it there keeps the sidebar). The trailing
 * slash matters, so a sibling like `/collections-archive` is not one.
 */
export function isWorkspaceRoute(pathname: string): boolean {
  return (
    pathname === ROUTES.collections ||
    pathname.startsWith(`${ROUTES.collections}/`) ||
    pathname === ROUTES.trash
  );
}

/**
 * The section lists outside the workspace that open items with their origin,
 * keyed for analytics, with the name the back arrow and first crumb show. The
 * label is looked up here, never stored on the entry, so it cannot drift.
 */
const OPENED_FROM_LABELS = {
  protocols: 'Protocols',
} as const;

export type OpenedFromKey = keyof typeof OPENED_FROM_LABELS;

export interface OpenedFrom {
  /** Which list, for analytics and the label. */
  readonly key: OpenedFromKey;
  /** The list's in-app path and query, so its search and filters come back. */
  readonly to: string;
}

/**
 * Router `state` for an item opened from a section list outside the workspace
 * (the Protocols page). The item page's trail starts at that
 * list instead of the folder path. Lives on the history entry, so refresh,
 * Back and Forward keep it; a fresh visit (new tab, pasted link) has none.
 */
export interface OpenedFromState {
  readonly openedFrom: OpenedFrom;
}

/** The list's name, as the back arrow and the first crumb show it. */
export function openedFromLabel(key: OpenedFromKey): string {
  return OPENED_FROM_LABELS[key];
}

const isOpenedFromKey = (key: unknown): key is OpenedFromKey =>
  typeof key === 'string' && Object.hasOwn(OPENED_FROM_LABELS, key);

/**
 * A backslash (the URL parser reads it as `/`) or any C0 control or DEL (it
 * strips tabs and newlines). Code points, not a regex: a control-character
 * class is what `no-control-regex` exists to question.
 */
const hasUnsafePathChar = (to: string) =>
  Array.from(to).some((ch) => {
    const code = ch.charCodeAt(0);
    return code <= 0x1f || code === 0x7f || ch === '\\';
  });
const PROBE_ORIGIN = 'https://app.invalid';

/**
 * An in-app path, safe as a link's raw `href` (a Cmd or middle click skips the
 * router and hands it to the browser). It starts with `/` (so no scheme and no
 * leading whitespace), has no backslash or control character (the URL parser
 * turns `/\host` into `//host` and drops tabs and newlines, so `/<tab>/host`
 * is `//host` too), and still resolves on this origin.
 */
export const isAppPath = (to: string) =>
  to.startsWith('/') && !hasUnsafePathChar(to) && new URL(to, PROBE_ORIGIN).origin === PROBE_ORIGIN;

/** The entry's origin list, or null when absent, malformed, unknown or not in-app. */
export function readOpenedFrom(state: unknown): OpenedFrom | null {
  if (typeof state !== 'object' || state === null) return null;
  const from = (state as { openedFrom?: unknown }).openedFrom;
  if (typeof from !== 'object' || from === null) return null;
  const { key, to } = from as { key?: unknown; to?: unknown };
  if (!isOpenedFromKey(key) || typeof to !== 'string' || !isAppPath(to)) return null;
  return { key, to };
}

/** The state a list puts on the entry it opens, from the list's own location. */
export function openedFromState(
  key: OpenedFromKey,
  location: { readonly pathname: string; readonly search: string },
): OpenedFromState {
  return { openedFrom: { key, to: `${location.pathname}${location.search}` } };
}
